import { ImageGallery, Product } from '@/types/pdp'
import {
  SUN,
  CASE,
  CLIPON,
  KIT,
  OPTICAL,
  ALL_ELECTRONICS_PRODUCT_TYPES,
  AVAILABLE,
  OUT_OF_STOCK,
  AVAILABLE_ANALYTICS,
  COMING_BACK_SOON,
  COMING_SOON,
  SOLD_OUT,
  CBS,
  CS,
  SO,
  NOT_FOR_SALE,
  PREORDER_AVAILABLE_ANALYTICS,
  NOT_AVAILABLE_ONLINE,
  AVAILABLE_ONE_LEFT,
  AVAILABLE_TWO_LEFT,
  AVAILABLE_FEW_LEFT,
} from '../constants/pdp'
import {
  WcsAddToBagParams,
  WcsPdpAvailability,
  WcsPdpCarouselItem,
  WcsPdpElectronicsAudioSystemFeatures,
  WcsPdpElectronicsConnectivityFeatures,
  WcsPdpProduct,
} from '../interfaces/normalizer'
import wcs from '@/scripts/libs/wcs'
export function getProductTypeByNumber(productTypeNumber: number): string {
  switch (Number(productTypeNumber)) {
    case 1:
      return SUN
    case 2:
      return OPTICAL
    case 3:
      return CASE
    case 4:
      return KIT
    case 12:
      return CLIPON
    default:
      return ''
  }
}

export function showDoubleImage(productType: string): boolean {
  const productTpesWithSingleImages = [KIT, CLIPON, CASE]
  return !productTpesWithSingleImages.includes(productType)
}

export const isProductStellaByProductType = (productType: string): boolean => {
  return ALL_ELECTRONICS_PRODUCT_TYPES.includes(productType)
}

export const findIndexOfVariantColorsInitialSlide = (
  carousel: WcsPdpCarouselItem[],
  partNumber: string
): number => {
  const foundIndex = carousel?.findIndex((product) => product?.partNumber === partNumber)
  return foundIndex
}

// TODO: change p.value with new carousel implementation
export const findSelectedItemInCarousel = (
  carousel: WcsPdpCarouselItem[],
  currentCatEntryId: string
): WcsPdpCarouselItem | undefined => {
  return carousel?.find((item) => item.catEntryId === currentCatEntryId)
}

export const mapSelectedTryLens = (
  carousel: WcsPdpCarouselItem[],
  currentCatEntryId: string
): string | undefined => {
  return findSelectedItemInCarousel(carousel, currentCatEntryId)?.draggableClen
}

export const prepareParamsForAddToBag = (
  frameCatentry: string,
  lensCatentry: string,
  mandatoryServices: string,
  isPickupStore: boolean,
  eyewearProtectionId: string
): WcsAddToBagParams => {
  const roxSet = [frameCatentry, lensCatentry]
  const mandatoryServicesList = mandatoryServices.split(',')
  mandatoryServicesList.forEach((mandatoryService) => roxSet.push(mandatoryService))
  const roxSet_1 = roxSet.join('_')

  const params: WcsAddToBagParams = {
    catEntryId_1: frameCatentry,
    catEntryId_2: lensCatentry,
    quantity_1: 1,
    quantity_2: 1,
    roxSet_1,
  }

  let counter = 3
  mandatoryServicesList.forEach((mandatoryService) => {
    params[`catEntryId_${counter}`] = mandatoryService
    params[`quantity_${counter}`] = 1
    counter++
  })

  if (eyewearProtectionId) {
    params[`catEntryId_${counter}`] = eyewearProtectionId
    params[`quantity_${counter}`] = 1
    params.eppSet_1 = `${frameCatentry}_${eyewearProtectionId}`
  }

  if (isPickupStore) {
    params.isPickupStore
  }

  return params
}

export const isProductOpticalOrSun = (productType: string): boolean => {
  return productType === OPTICAL || productType === SUN
}

export const getProductStatus = ({
  product,
  productAvailability,
}: {
  product: WcsPdpProduct
  productAvailability: WcsPdpAvailability
}): string => {
  if (!productAvailability) return ''

  const { roxLensPanel } = wcs.getStoreConfig()
  const metaShowsEmailMeWhenAvailable =
    product?.isSupernova && +productAvailability.inventory >= 10000 && !roxLensPanel.isEnabled
  if (metaShowsEmailMeWhenAvailable) {
    return COMING_BACK_SOON
  }

  if (productAvailability.oosStatus === CBS) {
    return COMING_BACK_SOON
  }
  if (productAvailability.oosStatus === CS) {
    return COMING_SOON
  }
  if (productAvailability.oosStatus === SO) {
    return SOLD_OUT
  }

  return productAvailability.isAddToBagEnabled ? AVAILABLE : NOT_AVAILABLE_ONLINE
}

/*- Out-of-stock → when oosStatus=CBS
- Coming soon → when oosStatus=CS
- SoldOut → default when oosStatus=SO
- Not-for-sale → when oosStatus=SO & isp=false & isLimitedEdition=false
*/

export const getProductStatusAnalytics = (
  productAvailability: WcsPdpAvailability,
  product?: Product
): string => {
  const hurryUpQuantity = +productAvailability.hurryUpQuantity
  if (hurryUpQuantity === 1) {
    return AVAILABLE_ONE_LEFT
  }
  if (hurryUpQuantity === 2) {
    return AVAILABLE_TWO_LEFT
  }
  if (hurryUpQuantity >= 3 && hurryUpQuantity <= 6) {
    return AVAILABLE_FEW_LEFT
  }
  if (product?.isPreOrder) {
    return PREORDER_AVAILABLE_ANALYTICS
  }
  if (productAvailability.isAddToBagEnabled === true) {
    return AVAILABLE_ANALYTICS
  }
  if (productAvailability.oosStatus === CBS) {
    return OUT_OF_STOCK
  }
  if (productAvailability.oosStatus === CS) {
    return COMING_SOON
  }
  if (productAvailability.oosStatus === SO && productAvailability.isAddToBagEnabled === false) {
    return SOLD_OUT
  }
  if (productAvailability.lifecycleVal === false) {
    return NOT_FOR_SALE
  }

  return AVAILABLE_ANALYTICS
}

export const replaceWrongSign = (url: string): string => url.replace(/&amp;/g, '&')

const COMMA_SPACE = ', '

export const mapElectronicsConnectivity = (
  connectivity: string
): WcsPdpElectronicsConnectivityFeatures | undefined => {
  if (!connectivity) {
    return undefined
  }

  const splitConnectivity = connectivity.split(COMMA_SPACE)
  return {
    wifi: splitConnectivity[0],
    bluetooth: splitConnectivity[1],
  }
}

export const ALT_IMAGE_TEXT = 'alt-image-text'

export const mapImagesGallery = (product: WcsPdpProduct): ImageGallery[] => {
  const firstImage = {
    src: product?.imageUrl,
    alt: product?.altText || ALT_IMAGE_TEXT,
    view: '',
  }

  if ([SUN, OPTICAL, ALL_ELECTRONICS_PRODUCT_TYPES].includes(product?.type)) {
    return [
      firstImage,
      {
        src: '',
        alt: '',
        view: '',
      },
      {
        src: '',
        alt: '',
        view: '',
      },
    ]
  } else {
    return [firstImage]
  }
}

const SLASH = '/'
export const mapElectronicsAudioSystem = (
  audioSystem: string
): WcsPdpElectronicsAudioSystemFeatures | undefined => {
  if (!audioSystem) {
    return undefined
  }
  const splitAudioSystem = audioSystem.split(SLASH)
  return {
    output: splitAudioSystem[0],
    input: splitAudioSystem[1],
  }
}

export const mapBridgeSize = (bridgeSize: string): string => {
  if (!bridgeSize) {
    return ''
  }

  return bridgeSize?.substring(0, 2) + ' ' + bridgeSize.substring(2, 4)
}
